import { useEffect, useRef, useCallback } from "react";

const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000; // Warn 2 min before

/**
 * Hook that tracks user idle time and triggers logout after 15 minutes of inactivity.
 * Shows a warning callback 2 minutes before expiry.
 * 
 * @param {Function} onTimeout - Called when session expires (should handle logout)
 * @param {Function} onWarning - Called 2 min before timeout (show warning)
 * @param {boolean} enabled - Whether to track idle (disable on public pages)
 */
export const useSessionTimeout = (onTimeout, onWarning, enabled = true) => {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  const resetTimers = useCallback(() => {
    if (!enabled) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    warningRef.current = setTimeout(() => {
      if (onWarning) onWarning();
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);

    timeoutRef.current = setTimeout(() => {
      if (onTimeout) onTimeout();
    }, SESSION_TIMEOUT_MS);
  }, [enabled, onTimeout, onWarning]);

  useEffect(() => {
    if (!enabled) return;

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
    const handler = () => resetTimers();

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetTimers(); // start on mount

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [enabled, resetTimers]);

  return { resetTimers };
};
